import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

const surgicalTreatments = [
  {
    id: "greffe-sinus",
    title: "Greffe sous-sinusienne",
    description:
      "Intervention qui consiste à augmenter la hauteur osseuse au niveau du sinus maxillaire, afin de permettre la pose d'implants dans une zone où l'os est insuffisant. Une technique précise, maîtrisée, réalisée en toute sécurité.",
    image: "/treatments/greffe-sinus.png",
  },
  {
    id: "greffe-osseuse",
    title: "Greffe d'apposition osseuse pré-implantaire",
    description:
      "Reconstruction du volume osseux avant la pose d'un implant, par apport de matériau de greffe. Cette étape permet de retrouver un support solide et durable pour l'implant à venir.",
    image: "/treatments/greffe-osseuse.png",
  },
  {
    id: "greffe-gencive",
    title: "Greffe épithélio-conjonctive",
    description:
      "Technique de chirurgie muco-gingivale destinée à renforcer et reconstituer la gencive, notamment en cas de récession gingivale. Elle protège les racines dentaires et améliore l'esthétique du sourire.",
    image: "/treatments/greffe-gencive.png",
  },
];

export const ChirurgieDetail = () => {
  const [showMore, setShowMore] = useState(false);
  return (
    <section
      id="chirurgie-detail"
      data-testid="chirurgie-detail-section"
      className="py-20 md:py-32 relative overflow-hidden"
    >
      <div className="container-xl">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left — Image collage */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="grid grid-cols-6 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="col-span-4 aspect-[3/4] rounded-2xl overflow-hidden relative group"
                data-testid="chirurgie-photo-main"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_smile-lagavotte/artifacts/199rc5cw_PSTK4747.JPG"
                  alt="Dr Vermot présentant un implant — gros plan"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D241E]/20 via-transparent to-transparent" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.8,
                  delay: 0.15,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="col-span-2 flex flex-col gap-4"
              >
                <div
                  className="aspect-square rounded-2xl overflow-hidden relative group"
                  data-testid="chirurgie-photo-scan"
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_smile-lagavotte/artifacts/noom2j49_PSTK4466-2%202.JPG"
                    alt="Diagnostic 3D en chirurgie implantaire"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div
                  className="flex-1 min-h-[160px] rounded-2xl overflow-hidden relative group"
                  data-testid="chirurgie-photo-composition"
                >
                  <img
                    src="/treatments/implant-composition.png"
                    alt="Composition d'un implant : vis + pilier + couronne"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right — Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 order-1 lg:order-2"
          >
            <div
              className="inline-flex items-center gap-2.5 bg-[#BE6C42] text-white rounded-full px-4 py-2"
              data-testid="chirurgie-specialty-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white" />
              <span className="text-[11px] uppercase tracking-[0.22em] font-semibold">
                Notre spécialité
              </span>
            </div>
            <h2 className="h2 text-3xl sm:text-4xl lg:text-5xl mt-6 leading-[1.05]">
              Qu&apos;est-ce qu&apos;un
              <br />
              <span className="italic text-[#BE6C42] font-light">
                implant dentaire ?
              </span>
            </h2>

            <div className="mt-8 pl-6 border-l-2 border-[#BE6C42]">
              <p className="body-text text-base sm:text-lg">
                C&apos;est une petite racine artificielle, placée dans l&apos;os
                et qui sert de base solide pour remplacer la ou les dents
                manquantes.
              </p>
            </div>

            <p className="body-text text-base sm:text-lg mt-6">
              Une solution{" "}
              <span className="text-[#2D241E] font-medium">fiable</span>,{" "}
              <span className="text-[#2D241E] font-medium">discrète</span> et{" "}
              <span className="text-[#2D241E] font-medium">durable</span>,
              pensée pour votre confort au quotidien.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: "3D", v: "Diagnostic précis" },
                { k: "Mini", v: "Invasif" },
                { k: "100%", v: "Sur-mesure" },
              ].map((stat) => (
                <div
                  key={stat.k}
                  className="border-t border-[#E8DED5] pt-4"
                  data-testid={`chirurgie-stat-${stat.k.toLowerCase()}`}
                >
                  <div className="font-serif text-3xl text-[#BE6C42] italic leading-none">
                    {stat.k}
                  </div>
                  <div className="mt-2 text-xs text-[#6A584E] leading-snug">
                    {stat.v}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* CTA + expandable list */}
        <div className="mt-16 md:mt-20 flex flex-col items-center">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onClick={() => setShowMore((v) => !v)}
            className="btn-primary"
            data-testid="toggle-surgical-treatments"
          >
            {showMore ? "Masquer les traitements" : "Voir tous les traitements proposés"}
            <ChevronDown
              size={18}
              className={`transition-transform duration-500 ${showMore ? "rotate-180" : ""}`}
            />
          </motion.button>

          <AnimatePresence initial={false}>
            {showMore && (
              <motion.div
                key="surgical-list"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden w-full"
                data-testid="surgical-treatments-list"
              >
                <div className="pt-14 grid md:grid-cols-3 gap-6 md:gap-8">
                  {surgicalTreatments.map((t, idx) => (
                    <motion.article
                      key={t.id}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: idx * 0.12,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      className="card-premium group flex flex-col overflow-hidden"
                      data-testid={`surgical-treatment-${t.id}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={t.image}
                          alt={t.title}
                          className="card-image absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#2D241E]/35 via-transparent to-transparent" />
                      </div>
                      <div className="p-7 flex flex-col flex-1">
                        <h4 className="font-serif text-xl md:text-[22px] text-[#2D241E] leading-tight mb-3">
                          {t.title}
                        </h4>
                        <p className="body-text text-[14px] leading-relaxed">
                          {t.description}
                        </p>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default ChirurgieDetail;
